import * as React from 'react';
import strings from '../../strings/strings.json';
import CourseCard, { PublishedCourseCard } from './components/CourseCard';
import { Course } from '../../models/Course';
import { breakpoints, sizes } from '../../theme';
import { BodyRegular, FlexColLg, FlexSpaceBetween, Headline3 } from 'components/Elements';
import { MainSectionWithGaps } from '../../components/sections/MainSection';
import PlusIcon from 'assets/icons/controls/PlusIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import styled from 'styled-components';
import Modal from 'components/modals/Modal';
import Header from 'components/sections/Header';
import { TabDefinition } from 'components/tabs/Tabs';
import ScheduleIcon from 'assets/icons/navigation/ScheduleIcon';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { AuthContext } from 'contextProviders/AuthContext';
import { useLocation } from 'react-router-dom';
import TabStructureComponent from 'components/tabs/TabStructureComponent';
import BuildIcon from 'assets/icons/BuildIcon';
import { TrainerDashboardContext, TrainerDashboardProvider } from 'contextProviders/TrainerDashboardContext';
import LiveCoursesTable from 'pages/OA/courses/LiveCoursesTable';
import BuildCourseSwitch from 'components/modals/createCourseModalComponents/BuildCourseSwitch';
import Button from 'components/buttons/Button';

type State = {
    activeTabId: string;
    activeRowId?: string;
};

export default function TrainerDashboardPage(): JSX.Element {
    return (
        <TrainerDashboardProvider>
            <TrainerDashboardPageContent />
        </TrainerDashboardProvider>
    );
}

function TrainerDashboardPageContent(): JSX.Element {
    const { state } = useLocation();
    const { activeTabId, activeRowId } = (state as State) ?? {};

    const { userData } = React.useContext(AuthContext);
    const { liveCourses, errors } = React.useContext(TrainerDashboardContext);

    const tabDefinitions: TabDefinition[] = [
        {
            id: 'liveCourses',
            name: strings.trainerDashboard.tabTitles.liveCourses,
            icon: ScheduleIcon,
            content: <LiveCoursesTable liveCourses={liveCourses} activeRowCourseId={activeRowId} hideTrainerColumn />,
        },
        {
            id: 'scheduleCourses',
            name: strings.trainerDashboard.tabTitles.scheduleCourses,
            icon: ScheduleIcon,
            content: <ScheduleCoursesTabContent />,
        },
        {
            id: 'buildCourses',
            name: strings.trainerDashboard.tabTitles.buildCourses,
            icon: BuildIcon,
            content: <BuildCoursesTabContent />,
        },
    ];

    if (errors)
        return (
            <>
                <Header title={strings.trainerDashboard.error} />
                <MainSectionWithGaps>
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.OADashboard.getOrgDataError.title}
                        description={strings.OADashboard.getOrgDataError.description}
                        maxWidth="100%"
                    />
                </MainSectionWithGaps>
            </>
        );

    return (
        <>
            <Header
                title={userData.organisation!.name}
                logoS3Filepath={userData.organisation!.logoS3Filepath}
                roleSwitch
            />
            <MainSectionWithGaps>
                <TabStructureComponent tabs={tabDefinitions} initialActiveTabId={activeTabId} />
            </MainSectionWithGaps>
        </>
    );
}

function ScheduleCoursesTabContent(): JSX.Element {
    const { courses } = React.useContext(TrainerDashboardContext);

    const publishedCourses = courses.filter((c) => c.published);

    return (
        <TabContent>
            <Headline3>{strings.trainerDashboard.tabTitles.scheduleCourses}</Headline3>
            {!publishedCourses.length ? (
                <BodyRegular>{strings.trainerDashboard.scheduleCourses.empty}</BodyRegular>
            ) : (
                <CourseCardsContainer>
                    {publishedCourses.map((course: Course) => {
                        return <PublishedCourseCard course={course} key={course.id} />;
                    })}
                </CourseCardsContainer>
            )}
        </TabContent>
    );
}

function BuildCoursesTabContent(): JSX.Element {
    const { courses, refetchCourses } = React.useContext(TrainerDashboardContext);

    const [openBuildNewCourseModal, setOpenBuildNewCourseModal] = React.useState<boolean>(false);

    const buildNewCourseButtonRef = React.useRef<HTMLButtonElement>(null);

    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        const someCoursesAreGenerating = courses.some((course) => {
            const courseIsGenerating =
                !!course.dateGenerateStart && !course.dateGenerateEnd && !course.dateGenerateError;
            return courseIsGenerating;
        });

        if (someCoursesAreGenerating && !intervalRef.current) {
            // Start polling
            intervalRef.current = setInterval(() => {
                refetchCourses();
            }, 4000);
        }
        if (!someCoursesAreGenerating && intervalRef.current) {
            // Stop polling
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [courses]); // eslint-disable-line

    async function closeBuildNewCourseModal() {
        setOpenBuildNewCourseModal(false);
        buildNewCourseButtonRef.current?.focus();
        await refetchCourses();
    }

    return (
        <TabContent>
            <BuildCoursesHeader>
                <Headline3>{strings.trainerDashboard.tabTitles.buildCourses}</Headline3>
                <Button
                    ref={buildNewCourseButtonRef}
                    title={strings.trainerDashboard.buildCourses.buildNewCourseButton.title}
                    aria={strings.trainerDashboard.buildCourses.buildNewCourseButton.aria}
                    icon={<PlusIcon size={IconSize.LARGE} />}
                    onClick={() => setOpenBuildNewCourseModal(true)}
                    colour='PRIMARY'
                />
            </BuildCoursesHeader>

            <CourseCardsContainer>
                {courses
                    .filter((c) => !c.published)
                    .map((course: Course) => {
                        return <CourseCard course={course} key={course.id} />;
                    })}
            </CourseCardsContainer>
            {openBuildNewCourseModal && (
                <Modal
                    onClickCloseButton={closeBuildNewCourseModal}
                    headlineText={strings.createCourseModal.createCourseHeadline}
                    modalBody={<BuildCourseSwitch closeModal={closeBuildNewCourseModal} />}
                />
            )}
        </TabContent>
    );
}

const TabContent = styled(FlexColLg)`
    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingMd};
    }
`;

const CourseCardsContainer = styled.div`
    display: grid;
    gap: ${sizes.spacingMd};
    grid-template-columns: repeat(2, 1fr);
    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const BuildCoursesHeader = styled(FlexSpaceBetween)`
    align-items: center;
    gap: ${sizes.spacingMd};
    flex-wrap: wrap;

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        align-items: start;
    }
`;
